import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const QuoteGenerator = ({ rfq, onGenerateQuote, onClose }) => {
  const [quoteData, setQuoteData] = useState({
    validityPeriod: '30',
    paymentTerms: 'net_30',
    deliveryTimeline: '',
    items: [
      {
        id: 1,
        description: '',
        quantity: 1,
        unitPrice: 0,
        total: 0
      }
    ],
    notes: '',
    terms: `1. This quotation is valid for the specified period.\n2. Prices are in Ugandan Shillings (UGX).\n3. Payment terms as specified.\n4. Delivery timeline subject to confirmation.\n5. Additional terms and conditions apply.`
  });

  const [totals, setTotals] = useState({
    subtotal: 0,
    tax: 0,
    total: 0
  });

  const validityOptions = [
    { value: '15', label: '15 Days' },
    { value: '30', label: '30 Days' },
    { value: '45', label: '45 Days' },
    { value: '60', label: '60 Days' }
  ];

  const paymentTermsOptions = [
    { value: 'immediate', label: 'Immediate Payment' },
    { value: 'net_15', label: 'Net 15 Days' },
    { value: 'net_30', label: 'Net 30 Days' },
    { value: '50_50', label: '50% Upfront, 50% on Delivery' },
    { value: 'milestone', label: 'Milestone-based' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const calculateTotals = (items) => {
    const subtotal = items?.reduce((sum, item) => sum + (item?.total || 0), 0);
    const tax = subtotal * 0.18; // 18% VAT
    const total = subtotal + tax;
    
    setTotals({ subtotal, tax, total });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...quoteData?.items];
    updatedItems[index] = { ...updatedItems?.[index], [field]: value };
    
    if (field === 'quantity' || field === 'unitPrice') {
      updatedItems[index].total = updatedItems?.[index]?.quantity * updatedItems?.[index]?.unitPrice;
    }
    
    setQuoteData({ ...quoteData, items: updatedItems });
    calculateTotals(updatedItems);
  };

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    };
    const updatedItems = [...quoteData?.items, newItem];
    setQuoteData({ ...quoteData, items: updatedItems });
  };

  const removeItem = (index) => {
    if (quoteData?.items?.length > 1) {
      const updatedItems = quoteData?.items?.filter((_, i) => i !== index);
      setQuoteData({ ...quoteData, items: updatedItems });
      calculateTotals(updatedItems);
    }
  };

  const handleGenerateQuote = () => {
    const quote = {
      rfqId: rfq?.id,
      customerId: rfq?.customer?.id,
      quoteNumber: `QT-${Date.now()}`,
      validUntil: new Date(Date.now() + parseInt(quoteData.validityPeriod) * 24 * 60 * 60 * 1000),
      paymentTerms: quoteData?.paymentTerms,
      deliveryTimeline: quoteData?.deliveryTimeline,
      items: quoteData?.items,
      subtotal: totals?.subtotal,
      tax: totals?.tax,
      total: totals?.total,
      notes: quoteData?.notes,
      terms: quoteData?.terms,
      createdAt: new Date(),
      status: 'draft'
    };
    
    onGenerateQuote(quote);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Generate Quote</h2>
            <p className="text-muted-foreground">RFQ #{rfq?.id} - {rfq?.customer?.name}</p>
          </div>
          <Button variant="ghost" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Quote Settings */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label="Validity Period"
                options={validityOptions}
                value={quoteData?.validityPeriod}
                onChange={(value) => setQuoteData({ ...quoteData, validityPeriod: value })}
              />
              <Select
                label="Payment Terms"
                options={paymentTermsOptions}
                value={quoteData?.paymentTerms}
                onChange={(value) => setQuoteData({ ...quoteData, paymentTerms: value })}
              />
              <Input
                label="Delivery Timeline"
                type="text"
                placeholder="e.g., 2-3 weeks"
                value={quoteData?.deliveryTimeline}
                onChange={(e) => setQuoteData({ ...quoteData, deliveryTimeline: e?.target?.value })}
              />
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground">Quote Items</h3>
                <Button variant="outline" onClick={addItem} iconName="Plus" iconPosition="left">
                  Add Item
                </Button>
              </div>

              <div className="space-y-4">
                {quoteData?.items?.map((item, index) => (
                  <div key={item?.id} className="grid grid-cols-12 gap-4 items-end p-4 border border-border rounded-lg">
                    <div className="col-span-5">
                      <Input
                        label="Description"
                        type="text"
                        placeholder="Item description"
                        value={item?.description}
                        onChange={(e) => handleItemChange(index, 'description', e?.target?.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        label="Quantity"
                        type="number"
                        min="1"
                        value={item?.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseInt(e?.target?.value) || 0)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        label="Unit Price (UGX)"
                        type="number"
                        min="0"
                        value={item?.unitPrice}
                        onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e?.target?.value) || 0)}
                      />
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground mb-1">Total</p>
                      <p className="font-semibold text-foreground">{formatCurrency(item?.total)}</p>
                    </div>
                    <div className="col-span-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        disabled={quoteData?.items?.length === 1}
                      >
                        <Icon name="Trash2" size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="mt-6 flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span className="font-medium">{formatCurrency(totals?.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">VAT (18%):</span>
                    <span className="font-medium">{formatCurrency(totals?.tax)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold border-t border-border pt-2">
                    <span>Total:</span>
                    <span>{formatCurrency(totals?.total)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Additional Notes</label>
              <textarea
                className="w-full p-3 border border-border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                rows="3"
                placeholder="Any additional notes for the customer..."
                value={quoteData?.notes}
                onChange={(e) => setQuoteData({ ...quoteData, notes: e?.target?.value })}
              />
            </div>

            {/* Terms & Conditions */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Terms & Conditions</label>
              <textarea
                className="w-full p-3 border border-border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                rows="5"
                value={quoteData?.terms}
                onChange={(e) => setQuoteData({ ...quoteData, terms: e?.target?.value })}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="secondary" iconName="Eye" iconPosition="left">
            Preview
          </Button>
          <Button onClick={handleGenerateQuote} iconName="Send" iconPosition="left">
            Generate Quote
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuoteGenerator;